/**
 * @module helper.array
 */
import { isArrayEmpty } from './isArrayEmpty';
import { removeIndex } from './removeIndex';

/**
 * Remove specific item that equal data or finded by finder from source.
 * 
 * @param finder A factory, data is second param, return a function just like `Array.find`.
 * 
 * ```ts
 * removeItem([1, '2', true, { a: 1 }], 0) => [1, '2', true, { a: 1 }]
 * removeItem([1, '2', true, { a: 1 }], 1) => ['2', true, { a: 1 }]
 * ```
 * 
 * ```ts
 * removeItem([{ a: 1 }, { a: 2 }, { a: 3 }, { a: 4 }], { a: 3 }, data => item => data.a === item.a)
 * =>
 * [{ a: 1 }, { a: 2 }, { a: 4 }]
 * ```
 */
export function removeItem<T>(
	source: T[],
	data: T,
	finder?: (data: T) => (item: T) => boolean
): T[] {
	if (!Array.isArray(source)) {
		throw new Error('source should be array.');
	} else if (isArrayEmpty(source)) {
		return source;
	}

	let index: number;

	if (finder) {
		if (typeof finder !== 'function') {
			throw new Error('finder should return a function that return boolean.');
		}

		const handler = finder(data);

		if (typeof handler !== 'function') {
			throw new Error('finder should return a function that return boolean.');
		}

		index = source.findIndex(handler);
	} else {
		index = source.indexOf(data);
	}

	if (index !== -1) {
		return removeIndex(source, index);
	}

	return source;
}
